import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {putAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {SameWidthButton} from '../../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {Dialog} from '../../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogButtonGroup} from '../../../../../libs/shared/ui/src/components/dialogs/dialogButtonGroup';
import {DialogHeader} from '../../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {getCustomEmojiShortcodes} from '../../../../store/data/customEmojis/customEmojiSelectors';
import {ButtonLoader} from './buttonLoader';
import {CustomEmojiInput} from './customEmojiInput';
import {CustomEmojiList} from './customEmojiList';

const FILE_MAX_SIZE = 512000; // 512 KB
const shortcodeRegexp = /^[\w+-]+$/;

/*
 * Constants.
 */

enum EmojiDialogSectionsEnum {
  CREATE = 'create',
  EDIT = 'edit',
}

const emojiDialogSections = [EmojiDialogSectionsEnum.CREATE, EmojiDialogSectionsEnum.EDIT];
const sectionTitle: {[T in EmojiDialogSectionsEnum]?: ReactNode} = {
  [EmojiDialogSectionsEnum.CREATE]: <Trans id="custom-emoji-dialog-create-tab">Create Emojis</Trans>,
  [EmojiDialogSectionsEnum.EDIT]: <Trans id="custom-emoji-dialog-edit-tab">Edit Emojis</Trans>,
};

/*
 * Props.
 */

interface CustomEmojiDialogProps {
  onRequestClose: () => void;
}

/*
 * Style.
 */

const StyledSidebarDiv = styled.div`
  grid-area: sidebar;
  display: grid;
  grid-row-gap: 15px;
  grid-auto-rows: max-content;
`;

const StyledSectionButton = styled(ChromelessButton)<{$isSelected: boolean}>`
  color: ${(p) => (p.$isSelected ? p.theme.greys.shade80 : p.theme.greys.shade60)};
  font-weight: ${(p) => (p.$isSelected ? fontWeights.medium : fontWeights.normal)};
  &:hover {
    color: ${(p) => p.theme.greys.shade80};
  }
`;

const StyledContentWrapper = styled.div`
  flex-grow: 1;
  display: grid;
  grid-template-areas:
    'sidebar section'
    'footer footer';
  grid-template-columns: 130px 1fr;
  grid-column-gap: 40px;
  grid-template-rows: minmax(0, 1fr) auto;
  overflow: auto;
  padding: 24px 24px 0 24px;
`;

const StyledTooltip = styled.div`
  margin-top: 0;
  margin-bottom: 24px;
  line-height: 20px;
`;

const StyledCustomEmojiForm = styled.form`
  grid-area: section;
  display: flex;
  flex-direction: column;
`;

const StyledFooterDiv = styled.div`
  grid-area: footer;
`;

const StyledSeparator = styled.div`
  height: 1px;
  background-color: ${(p) => p.theme.greys.shade30};
  margin-bottom: 20px;
`;

/*
 * Component.
 */

export const CustomEmojiDialog: FC<CustomEmojiDialogProps> = ({onRequestClose}) => {
  const [shortcode, setShortcode] = useState<string>();
  const [selectedSection, setSelectedSection] = useState(EmojiDialogSectionsEnum.CREATE);
  const [file, setFile] = useState<File>();
  const [isLoading, toggleIsLoading] = useToggle();
  const [serverError, setServerError] = useState<string>();
  const companyUrl = useAppSelector(getCompanyUrl);
  const customEmojiShortcodes = useAppSelector(getCustomEmojiShortcodes);
  const error = getError(shortcode, file, customEmojiShortcodes) || serverError;
  const canCreate = Boolean(shortcode && file && !error);

  const onCreateClick = () => {
    if (!canCreate || !file) {
      return;
    }

    const fd = new FormData();
    fd.append('emoji', file);

    toggleIsLoading();
    putAsync(`${companyUrl}/custom_emojis/${shortcode}`, fd).then(
      () => {
        toggleIsLoading();
        onRequestClose();
      },
      (err) => {
        toggleIsLoading();
        setServerError('Unexpected error while trying to create the emoji.');
      },
    );
  };

  return (
    <Dialog width={580} height={475}>
      <DialogHeader
        title={selectedSection === EmojiDialogSectionsEnum.CREATE ? 'New Custom Emoji' : 'Edit Emojis'}
        onRequestClose={onRequestClose}
      />
      <StyledContentWrapper>
        {renderSidebar(selectedSection, setSelectedSection)}
        {renderContent(selectedSection, setShortcode, setFile, shortcode, error)}
      </StyledContentWrapper>
      {maybeRenderFooter(selectedSection, onRequestClose, onCreateClick, canCreate, isLoading)}
    </Dialog>
  );
};

/*
 * Helpers.
 */

function renderSidebar(
  selectedSection: EmojiDialogSectionsEnum,
  onSectionClick: (arg: EmojiDialogSectionsEnum) => void,
) {
  return (
    <StyledSidebarDiv>
      {emojiDialogSections.map((section) => (
        <StyledSectionButton
          key={section}
          onClick={() => onSectionClick(section)}
          $isSelected={section === selectedSection}
        >
          {sectionTitle[section]}
        </StyledSectionButton>
      ))}
    </StyledSidebarDiv>
  );
}

function renderContent(
  selectedSection: EmojiDialogSectionsEnum,
  setShortcode: (arg: string) => void,
  setFile: (arg: File | undefined) => void,
  shortcode?: string,
  error?: string,
) {
  switch (selectedSection) {
    case EmojiDialogSectionsEnum.CREATE:
      return (
        <StyledCustomEmojiForm>
          <StyledTooltip>
            <Trans id="custom-emoji-dialog-creation-explanation">
              Custom emojis will be available to everyone in your organization! You can find these under the
              star icon in the emoji picker.
            </Trans>
          </StyledTooltip>
          <CustomEmojiInput onChange={setFile} />
          <TextBox
            label={<Trans id="custom-emoji-dialog-field-shortcode">Emoji name</Trans>}
            value={shortcode ?? ''}
            onChange={(text) => setShortcode(text)}
            hasError={Boolean(error)}
            errorMessage={error}
            placeholder="front-emoji"
            shouldAutoFocus
          />
        </StyledCustomEmojiForm>
      );

    case EmojiDialogSectionsEnum.EDIT:
      return <CustomEmojiList />;

    default:
      return null;
  }
}

function maybeRenderFooter(
  selectedSection: EmojiDialogSectionsEnum,
  onRequestClose: () => void,
  onCreateClick: () => void,
  canCreate: boolean,
  isLoading: boolean,
) {
  if (selectedSection !== EmojiDialogSectionsEnum.CREATE) {
    return null;
  }

  return (
    <StyledFooterDiv>
      <StyledSeparator />
      <DialogButtonGroup>
        <ButtonGroupSpacer />
        <SameWidthButton onClick={onRequestClose}>
          <Trans id="dialog-cancel">Cancel</Trans>
        </SameWidthButton>
        <SameWidthButton
          isDisabled={!canCreate || isLoading}
          onClick={onCreateClick}
          variant={ButtonVariantsEnum.PRIMARY}
        >
          <Trans id="custom-emoji-dialog-create-button">{isLoading ? <ButtonLoader /> : 'Create'}</Trans>
        </SameWidthButton>
      </DialogButtonGroup>
    </StyledFooterDiv>
  );
}

function isValidShortcode(shortcode: string) {
  return shortcodeRegexp.test(shortcode);
}

function getError(
  shortcode: string | undefined,
  file: File | undefined,
  existingShortcode: ReadonlyArray<string>,
) {
  if (file && file.size > FILE_MAX_SIZE) {
    return 'The uploaded image is too large';
  }

  if (!shortcode) {
    return null;
  }

  if (!isValidShortcode(shortcode)) {
    return 'The shortcode parameter can only contain letters, "_", "+", and "-"';
  }

  if (existingShortcode.includes(shortcode)) {
    return 'There is already an emoji with this shortcode';
  }

  return null;
}
